import { useCallback, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import { DataTags } from 'components/DataTags';
import { useDiscardConfirmation } from 'components/DiscardConfirmation';
import { FIELD_COLUMNS, type TFieldRowData } from 'constants/Field.constants';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import { capitalize, isEmpty } from 'lodash';
import {
  checkFirstSaveDisabled,
  checkSecondSaveDisabled,
  copyField,
  createUnsavedField,
  deleteField,
  fetchAdvancedSettingsForField,
  fieldMappingSelector,
  fieldsLoading,
  fieldsSelector,
  getFirstDrawer,
  setCurrentFieldCompare,
  setSelectedField,
} from 'slices/field.slice';
import {
  selectObjectTypesList,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { EFieldType, type TField } from '@timeedit/types';
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilterField,
} from '@timeedit/ui-components';

export const FieldTable = () => {
  const dispatch = useAppDispatch();
  const objectTypes = useAppSelector(selectObjectTypesList);
  const [contentRef, defaultTableProps] = useDefaultTableProps();
  const fields: TField[] = useAppSelector(fieldsSelector);
  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const fieldMap: Record<string, TField> = useAppSelector(fieldMappingSelector);

  const [_, __, ___, confirmDiscardChanged] = useDiscardConfirmation();
  const [highlightIds, setHighlightIds] = useState<number[]>([]);
  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const [filter, setFilter] = useState<any>({
    filters: {},
    freeTextSearch: '',
    sorts: {},
  });
  const loading = useAppSelector(fieldsLoading);

  const dataSource = useMemo<TFieldRowData[]>(() => {
    return fields.map((field) => {
      return {
        id: field.id,
        name: field.name === '' ? 'New field' : field.name,
        extId: field.extId,
        types: field.types,
        objectTypes: field.types.map((id) => objectTypesMap[id]?.name),
        fieldType: capitalize(field.fieldType),
      };
    });
  }, [fields, objectTypesMap]);

  const dataFiltered = useMemo(() => {
    const { freeTextSearch, filters = {} } = filter;
    if (!freeTextSearch && !filters?.type && !filters?.objectType) {
      return dataSource;
    }
    return dataSource.filter((item) => {
      if (filters.type && item.fieldType !== filters.type) {
        return false;
      }
      if (filters.objectType && !item.types.includes(filters.objectType)) {
        return false;
      }
      if (
        freeTextSearch &&
        !JSON.stringify({
          name: item.name,
          extId: item.extId,
          id: item.id,
        })
          .toLocaleLowerCase()
          .includes(freeTextSearch?.toLocaleLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [dataSource, filter]);

  const filterOptions = useMemo(() => {
    const listOptions = Object.values(EFieldType)
      .map((i: string) => {
        return {
          label: capitalize(i),
          value: capitalize(i),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    const objectTypesOptions = objectTypes
      .map((i) => {
        return {
          label: capitalize(i.name),
          value: i.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return {
      type: listOptions,
      objectType: objectTypesOptions,
    };
  }, [objectTypes]);

  const setFirstDrawer = useCallback(
    (data?: TField, checkChanged = false) => {
      const isFirstSaveDisabled = dispatch(checkFirstSaveDisabled());
      if (checkChanged && !isFirstSaveDisabled) {
        confirmDiscardChanged(() => {
          dispatch(setSelectedField(data));
        });
        return;
      }
      dispatch(setSelectedField(data));
    },
    [confirmDiscardChanged, dispatch]
  );

  const setSecondDrawer = useCallback(
    (data?: TField, checkChanged = false) => {
      const isSecondSaveDisabled = dispatch(checkSecondSaveDisabled());
      if (checkChanged && !isSecondSaveDisabled) {
        confirmDiscardChanged(() => {
          dispatch(setCurrentFieldCompare(data));
        });
        return;
      }
      dispatch(setCurrentFieldCompare(data));
    },
    [confirmDiscardChanged, dispatch]
  );

  const onRowClick = useCallback(
    ({ id }: TFieldRowData) => {
      const firstDrawer = dispatch(getFirstDrawer());
      if (firstDrawer && firstDrawer.id === id) {
        return setFirstDrawer(undefined, true);
      }
      if (id) {
        dispatch(fetchAdvancedSettingsForField(id));
      }
      setFirstDrawer(fieldMap[id], true);
    },
    [fieldMap, dispatch]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onCompare = useCallback(
    (record: TFieldRowData) => {
      const firstDrawer = dispatch(getFirstDrawer());
      if (!isEmpty(firstDrawer)) {
        setSecondDrawer(fieldMap[record.id], true);
      } else {
        setFirstDrawer(fieldMap[record.id], true);
      }
    },
    [fieldMap, dispatch]
  );

  const onDelete = useCallback(
    (record: TFieldRowData) => {
      confirmDelete(record.name, () => {
        dispatch(deleteField(fieldMap[record.id]));
      });
    },
    [confirmDelete, dispatch, fieldMap]
  );

  const onCreateNew = useCallback(() => {
    const isFirstSaveDisabled = dispatch(checkFirstSaveDisabled());
    if (!isFirstSaveDisabled) {
      confirmDiscardChanged(() => {
        dispatch(createUnsavedField());
      });
      return;
    }
    dispatch(createUnsavedField());
  }, [confirmDiscardChanged, dispatch]);

  const onDuplicate = useCallback(
    (field: TFieldRowData) => {
      const isFirstSaveDisabled = dispatch(checkFirstSaveDisabled());
      if (!isFirstSaveDisabled) {
        confirmDiscardChanged(() => {
          dispatch(copyField(fieldMap[field.id]));
        });
        return;
      }
      dispatch(copyField(fieldMap[field.id]));
    },
    [fieldMap, dispatch]
  );

  const footer = useCallback(() => {
    return (
      <div className="footer-action">
        <Button size="small" type="primary" onClick={onCreateNew}>
          {getText('create_new')}
        </Button>
      </div>
    );
  }, [onCreateNew]);

  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(getFirstDrawer())),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: TFieldRowData) =>
      getMenuProps<TFieldRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const title = useTableTitle<TFieldRowData>({
    total: fields.length,
    filteredAmount: dataFiltered.length,
  });

  return (
    <div className="field-table--container" ref={contentRef}>
      <Filter
        defaultFields={defaultFieldsFilter}
        onChange={setFilter}
        optionalFields={[] as any}
        options={filterOptions as any}
        settings={filter as any}
      />
      <EditableTable<TFieldRowData>
        loading={loading}
        columns={FIELD_COLUMNS}
        dataSource={dataFiltered}
        onRow={onRow}
        footer={footer}
        getMenuProps={menuProps}
        title={title}
        {...defaultTableProps}
      />
      {contextHolder}
    </div>
  );
};

const defaultFieldsFilter: TEIFilterField[] = [
  {
    key: 'type',
    label: 'Type',
    multiselect: false,
    type: TEEFilterType.text,
  },
  {
    key: 'objectType',
    label: 'Object Type',
    multiselect: false,
    type: TEEFilterType.text,
  },
];
